import { addNewUserValidations } from '../../field-user-management/user-management/Body/Form/validations';
import { ComboBox } from 'src/components/ui/combobox';
import { DealersService } from 'src/services/dealers.service';
import DialogModal from 'src/components/ui/dialogModal';
import Dropdown from 'src/components/ui/dropdownMenuTable/dropdownMenuTable';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import Input from 'src/components/forms/input';
import { IUser } from 'src/utils/interfaces/user';
import { IUserData } from '../../field-user-management/user-management/Body/Body';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import { NUMBER_ZERO } from 'src/utils/constants';
import { Permissions } from 'src/utils/enums/permissions.enum';
import ProfileOptionsIcon from 'src/components/dealer-mgmt/profile-options-icon';
import Spinner from 'src/components/ui/spinner';
import { useFormik } from 'formik';
import { useLocalization } from 'src/utils/useLocalization';
import { useTranslation } from 'react-i18next';
import {
    addHyphens,
    onKeyPressPhoneHandler,
} from '../../field-user-management/user-management/Body/Form/Form';
import {
    DealerManagementService,
    IEditDealerUserPayload,
} from 'src/services/dealer-management.service';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './styles.scss';

interface IISMUpdateUserProps {
    backClick: (
        didEdit: boolean,
        shouldLoadList?: boolean,
        didDelete?: boolean,
    ) => void;
    username: string;
    setErrorNotification: React.Dispatch<React.SetStateAction<boolean>>;
}

interface IPermissionCheckbox {
    label: string;
    value: string;
}

enum UserActionModalStatus {
    DELETE = 'DELETE',
    SUSPEND = 'SUSPEND',
    REINSTATE = 'REINSTATE',
}

const MenuItem = ({
    active,
    suspendLabel,
    reinstateLabel,
    deleteLabel,
    suspendHandler,
    reinstateHandler,
    deleteHandler,
}: {
    active: boolean;
    suspendLabel: string;
    reinstateLabel: string;
    deleteLabel: string;
    suspendHandler: () => void;
    reinstateHandler: () => void;
    deleteHandler: () => void;
}) => (
    <>
        {active && (
            <li className='menu-items' onClick={suspendHandler}>
                <span>{suspendLabel}</span>
            </li>
        )}
        {!active && (
            <li className='menu-items' onClick={reinstateHandler}>
                <span>{reinstateLabel}</span>
            </li>
        )}
        <li className='menu-items' onClick={deleteHandler}>
            <span style={{ color: 'rgb(228, 0, 44)' }}>{deleteLabel}</span>
        </li>
    </>
);

const ISMUpdateUser: React.FC<IISMUpdateUserProps> = ({
    backClick,
    username,
    setErrorNotification,
}) => {
    const { i18n } = useTranslation();
    const dispatch = useDispatch();
    const { ismAddNewUser, ismUpdateUser, editUser, systemAdmin } =
        useLocalization();
    const { dealerDataInfo } = useSelector((state: IUser) => state);
    const brand: string = dealerDataInfo?.dealer.brand?.toLowerCase() || 'vw';

    const [userData, setUserData] = useState<
        IUserData & {
            userId: string;
            userPermissions: Array<any>;
            associations: Array<any>;
            userIsFieldAccessUser: boolean;
            country: string;
        }
    >();
    const [shouldLoadList, setShouldLoadList] = useState<boolean>(false);
    const [isUserActive, setIsUserActive] = useState<boolean>(true);
    const [updateLoading, setUpdateLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [isCountryTouched, setIsCountryTouched] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>(
        ismUpdateUser.NOTIFICATION_SUCCESS_SAVE,
    );
    const [successNotificationType, setSuccessNotificationType] =
        useState<NotificationType>(NotificationType.SUCCESS);
    const [openDrop, setOpenDrop] = useState<boolean>(false);
    const [modalOpen, setModalOpen] = useState<string>('');
    const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
    const isMountedRef = useRef<boolean>(true);

    const [selectedPermissions, setSelectedPermissions] = useState<
        Set<Permissions>
    >(new Set());

    const dealerId = useSelector((state: IUser) => {
        return state.currentDealers?.default ?? '';
    });

    const permissionGroups = useMemo(
        () => ({
            leftCol: [
                {
                    title: ismAddNewUser.PERMISSIONS_INTERNAL_TOOLS,
                    items: [
                        {
                            label: ismAddNewUser.PERMISSIONS_INTERNAL_SECURITY,
                            value: Permissions.INTERNAL_SECURITY_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_FIELD_USER_MGMT,
                            value: Permissions.FIELD_ACCESS_MAINTENANCE,
                        },
                    ] as IPermissionCheckbox[],
                },
                {
                    title: ismAddNewUser.PERMISSIONS_DEALER_TOOLS,
                    items: [
                        {
                            label: ismAddNewUser.PERMISSIONS_DEALER_ACTIVATION,
                            value: Permissions.DEALER_ACTIVATION,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_DEALER_SECURITY,
                            value: Permissions.DEALER_SECURITY_MAINTENANCE,
                        },
                    ] as IPermissionCheckbox[],
                },
            ],
            rightCol: [
                {
                    title: ismAddNewUser.PERMISSIONS_CONTENT_TOOLS,
                    items: [
                        {
                            label: ismAddNewUser.PERMISSIONS_NEWS_STORIES,
                            value: Permissions.NEWS_STORY_MANAGEMENT,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_ONLINE_BULLETINS,
                            value: Permissions.ONLINE_BULLETINS_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_DISTRIBUTION_RULE,
                            value: Permissions.DISTRIBUTION_RULE_MANAGEMENT,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_STATIC_CONTENT,
                            value: Permissions.CONTENT_MAINTENANCE_PERMISSION,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_CV_FORM,
                            value: Permissions.CV_FORM_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_EDOCS,
                            value: Permissions.E_DOCUMENT_MAINTENANCE,
                        },
                        {
                            label: ismAddNewUser.PERMISSIONS_VEHICLE_MAINTENANCE_PERM,
                            value: Permissions.VEHICLE_MAINTENANCE,
                        },
                    ] as IPermissionCheckbox[],
                },
            ],
        }),
        [ismAddNewUser],
    );

    const countryOptions = [
        { value: 'USA', label: 'USA' },
        { value: 'Canada', label: 'Canada' },
    ];
    const countryLabelToValue = useMemo(
        () => new Map(countryOptions.map((o) => [o.label, o.value])),
        [],
    );
    const countryValueToLabel = useMemo(
        () => new Map(countryOptions.map((o) => [o.value, o.label])),
        [],
    );
    const countryOptionLabels = countryOptions.map((o) => o.label);

    async function getDealerUserData() {
        const service = new DealersService();
        try {
            const res = await service.getUserData(username, dealerId);
            if (!isMountedRef.current) {
                return;
            }
            if (res?.data) {
                const user = mapUserDataResponse(res.data);
                setSelectedPermissions(
                    new Set(
                        user.userPermissions
                            .map((p: any) =>
                                p.dealerId === null
                                    ? (p.permission as Permissions)
                                    : null,
                            )
                            .filter(Boolean),
                    ),
                );
                setUserData(user);
                setIsCountryTouched(false);
                setIsUserActive(isStatusActive(user.userStatus));
                return;
            } else {
                throw Error();
            }
        } catch {
            if (!isMountedRef.current) {
                return;
            }
            dispatch({
                type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
            });
            backClick(false);
            setErrorNotification(true);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }

    useEffect(() => {
        isMountedRef.current = true;
        setLoading(true);
        getDealerUserData();

        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const handleActiveModal = () => {
        if (isUserActive) {
            dispatch({
                type: MODAL_CONTROL_ACTIONS.SET_ACTIVE,
                payload: {
                    titleContent: ismUpdateUser.MODAL_TITLE,
                    bodyContent: ismUpdateUser.MODAL_CONTENT,
                    cancelBtnLabel: ismUpdateUser.MODAL_CANCEL,
                    confirmBtnLabel: ismUpdateUser.MODAL_CONFIRM,
                    onCancelHandler: cancelHandler,
                    onConfirmHandler: confirmHandler,
                },
            });
        } else {
            dispatch({
                type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
            });
        }
    };

    useEffect(() => {
        handleActiveModal();
    }, [isUserActive]);

    useEffect(() => {
        i18n.on('languageChanged', handleActiveModal);
        return () => {
            i18n.off('languageChanged', handleActiveModal);
        };
    }, []);

    const cancelHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: { shouldShow: false },
        });
    };

    const confirmHandler = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
        });
        setErrorNotification(false);
        backClick(false, shouldLoadList);
    };

    const headerBackClickHandler = () => {
        if (!isUserActive) {
            confirmHandler();
            return;
        }

        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_SHOW,
            payload: {
                shouldShow: true,
                onCancelHandler: cancelHandler,
                onConfirmHandler: confirmHandler,
            },
        });
    };

    const onChangePhoneHandler = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const formattedPhone = addHyphens(event.target.value);
        setFieldValue(event.target.name, formattedPhone);
    };

    const handlePermissionChange = (permission: Permissions) => {
        setSelectedPermissions((prev) => {
            const next = new Set(prev);
            if (next.has(permission)) {
                next.delete(permission);
            } else {
                next.add(permission);
            }
            return next;
        });
    };

    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        handleBlur,
        setFieldValue,
    } = useFormik({
        enableReinitialize: true,
        initialValues: {
            country: userData?.country || '',
            phone: addHyphens(userData?.phone || ''),
            email: userData?.email || '',
            firstName: userData?.firstName || '',
            lastName: userData?.lastName || '',
        },
        validationSchema: addNewUserValidations,
        onSubmit: handleFormSubmit,
    });

    const selectedCountryLabel =
        values.country !== ''
            ? countryValueToLabel.get(values.country) ?? values.country
            : '';

    const handleCountrySelect = (countryLabel: string) => {
        const countryCode = countryLabelToValue.get(countryLabel) ?? '';
        setIsCountryTouched(true);
        setFieldValue('country', countryCode);
    };

    const countryHasError = isCountryTouched && values.country === '';
    const countryErrorMessage = countryHasError
        ? i18n.t('Label.Field.Error.Required')
        : '';

    const isEdited = (): boolean => {
        if (!userData) {
            return false;
        }

        const initialPermissions = new Set(
            userData.userPermissions.map(
                (p: any) => p.permission as Permissions,
            ),
        );
        const hasDifferentValues =
            values.firstName !== userData.firstName ||
            values.lastName !== userData.lastName ||
            values.country !== (userData.country || '') ||
            values.phone.replace(/\D/g, '') !==
                userData.phone.replace(/\D/g, '');

        const hasDifferentPermissions =
            selectedPermissions.size !== initialPermissions.size ||
            Array.from(selectedPermissions).some(
                (permission) => !initialPermissions.has(permission),
            );

        return hasDifferentValues || hasDifferentPermissions;
    };

    const isActionDisabled =
        values.country === '' ||
        values.email === '' ||
        values.firstName === '' ||
        values.lastName === '' ||
        values.phone === '' ||
        !!Object.keys(errors).length ||
        selectedPermissions.size === NUMBER_ZERO ||
        updateLoading ||
        !isUserActive ||
        !isEdited();

    const handleActionModalCancel = () => {
        setModalOpen('');
    };

    const handleSuspendModalOpen = () => {
        setOpenDrop(false);
        setModalOpen(UserActionModalStatus.SUSPEND);
    };

    const handleReinstateModalOpen = () => {
        setOpenDrop(false);
        setModalOpen(UserActionModalStatus.REINSTATE);
    };

    const handleDeleteModalOpen = () => {
        setOpenDrop(false);
        setModalOpen(UserActionModalStatus.DELETE);
    };

    const reinstateUserConfirm = () => {
        if (!userData) {
            return;
        }

        const ACTIVATED_STATUS = 0;
        setModalIsLoading(true);
        setIsError(false);
        setIsSuccess(false);

        const service = new DealerManagementService();
        service
            .updateUserStatus(userData.userId, ACTIVATED_STATUS)
            .then(async (response) => {
                if (response?.status !== HttpStatusCode.SUCCESS) {
                    throw Error();
                }

                await getDealerUserData();
                setShouldLoadList(true);
                setSuccessNotificationType(NotificationType.SUCCESS);
                setSuccessMessage(ismUpdateUser.NOTIFICATION_SUCCESS_REINSTATE);
                setIsSuccess(true);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => {
                setModalIsLoading(false);
                handleActionModalCancel();
            });
    };

    const suspendUserConfirm = () => {
        if (!userData) {
            return;
        }

        const SUSPENDED_STATUS = 1;
        setModalIsLoading(true);
        setIsError(false);
        setIsSuccess(false);

        const service = new DealerManagementService();
        service
            .updateUserStatus(userData.userId, SUSPENDED_STATUS)
            .then(async (response) => {
                if (response?.status !== HttpStatusCode.SUCCESS) {
                    throw Error();
                }

                await getDealerUserData();
                setShouldLoadList(true);
                setSuccessNotificationType(NotificationType.ERROR);
                setSuccessMessage(ismUpdateUser.NOTIFICATION_SUCCESS_SUSPEND);
                setIsSuccess(true);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => {
                setModalIsLoading(false);
                handleActionModalCancel();
            });
    };

    const deleteUserConfirm = () => {
        if (!userData) {
            return;
        }

        setModalIsLoading(true);
        setIsError(false);

        const service = new DealerManagementService();
        service
            .deleteUser(userData.userId, userData.email)
            .then((response) => {
                if (response?.status !== HttpStatusCode.SUCCESS) {
                    throw Error();
                }

                dispatch({
                    type: MODAL_CONTROL_ACTIONS.SET_INACTIVE,
                });
                backClick(false, true, true);
            })
            .catch(() => {
                setIsError(true);
            })
            .finally(() => {
                setModalIsLoading(false);
                handleActionModalCancel();
            });
    };

    const getActionModalTitle = () => {
        if (modalOpen === UserActionModalStatus.SUSPEND) {
            return ismUpdateUser.ACTION_MODAL_TITLE_SUSPEND;
        }

        if (modalOpen === UserActionModalStatus.REINSTATE) {
            return ismUpdateUser.ACTION_MODAL_TITLE_REINSTATE;
        }

        if (modalOpen === UserActionModalStatus.DELETE) {
            return ismUpdateUser.ACTION_MODAL_TITLE_DELETE;
        }

        return '';
    };

    const getActionModalBody = () => {
        if (modalOpen === UserActionModalStatus.SUSPEND) {
            return ismUpdateUser.ACTION_MODAL_BODY_SUSPEND;
        }

        if (modalOpen === UserActionModalStatus.REINSTATE) {
            return ismUpdateUser.ACTION_MODAL_BODY_REINSTATE;
        }

        if (modalOpen === UserActionModalStatus.DELETE) {
            return ismUpdateUser.ACTION_MODAL_BODY_DELETE;
        }

        return '';
    };

    const getActionConfirmLabel = () => {
        if (modalOpen === UserActionModalStatus.SUSPEND) {
            return ismUpdateUser.ACTION_MODAL_CONFIRM;
        }

        if (modalOpen === UserActionModalStatus.REINSTATE) {
            return ismUpdateUser.ACTION_MODAL_CONFIRM;
        }

        if (modalOpen === UserActionModalStatus.DELETE) {
            return ismUpdateUser.ACTION_MODAL_CONFIRM;
        }

        return '';
    };

    async function handleFormSubmit(): Promise<void> {
        if (selectedPermissions.size === NUMBER_ZERO || values.country === '') {
            setIsError(true);
            return;
        }

        setUpdateLoading(true);
        setIsError(false);
        setIsSuccess(false);

        const dealerIds = (userData?.associations ?? []).map(
            (association: any) => association.dealerId as string,
        );
        const permissionSet = new Set(selectedPermissions);

        const isDealerSecurity = permissionSet.has(
            Permissions.DEALER_SECURITY_MAINTENANCE,
        );

        if (isDealerSecurity) {
            permissionSet.add(Permissions.USER_ADMIN);
        } else {
            permissionSet.delete(Permissions.USER_ADMIN);
        }

        const newPermissions = Array.from(permissionSet).map((permission) => ({
            dealerId: null,
            permission,
        }));

        const newAssociations = dealerIds.map((dId) => ({ dealerId: dId }));

        try {
            const service = new DealerManagementService();
            const request: IEditDealerUserPayload = {
                firstName: values.firstName.trim(),
                lastName: values.lastName.trim(),
                emailAddress: values.email.toLowerCase(),
                currentEmailAddress: (userData?.email ?? '').toLowerCase(),
                userId: userData?.userId ?? '',
                phoneNumber: values.phone.replace(/\D/g, ''),
                country: values.country,
                newPermissions,
                newAssociations: userData?.userIsFieldAccessUser
                    ? []
                    : newAssociations,
            };
            if (userData?.userIsFieldAccessUser) {
                request.isFieldUser = true;
                request.updateMode = 'FIELD_USER_GLOBAL_PERMISSIONS_ONLY';
            }
            await service.editUser(request);

            await getDealerUserData();
            setShouldLoadList(true);
            setSuccessNotificationType(NotificationType.SUCCESS);
            setSuccessMessage(ismUpdateUser.NOTIFICATION_SUCCESS_SAVE);
            setIsSuccess(true);
        } catch {
            setIsError(true);
        } finally {
            setUpdateLoading(false);
        }
    }

    const renderPermissionGroup = (
        groupTitle: string,
        items: IPermissionCheckbox[],
    ) => (
        <div className='edit-user-container__permission-group'>
            <span className='edit-user-container__permission-group-title'>
                {groupTitle}
            </span>
            {items.map((item) => (
                <label key={item.value} className='c-checkbox'>
                    <input
                        className='c-checkbox__input'
                        type='checkbox'
                        value={item.value}
                        checked={selectedPermissions.has(
                            item.value as Permissions,
                        )}
                        onChange={() =>
                            handlePermissionChange(item.value as Permissions)
                        }
                    />
                    <span className='c-checkbox__label'>{item.label}</span>
                </label>
            ))}
        </div>
    );

    return (
        <div>
            <DialogModal
                titleContent={getActionModalTitle()}
                bodyContent={getActionModalBody()}
                open={modalOpen !== ''}
                cancelBtnLabel={ismUpdateUser.MODAL_CANCEL}
                confirmBtnLabel={getActionConfirmLabel()}
                onCancelHandler={handleActionModalCancel}
                onConfirmHandler={() => {
                    if (modalOpen === UserActionModalStatus.SUSPEND) {
                        suspendUserConfirm();
                        return;
                    }

                    if (modalOpen === UserActionModalStatus.REINSTATE) {
                        reinstateUserConfirm();
                        return;
                    }

                    if (modalOpen === UserActionModalStatus.DELETE) {
                        deleteUserConfirm();
                    }
                }}
                isLoading={modalIsLoading}
                keepOpenOnConfirm={true}
            />
            <div className='edit-user-container'>
                <div className={`edit-user-container__header ${brand}`}>
                    <div className='one'>
                        <button
                            className='c-btn c-btn--link c-btn--small c-btn--simple'
                            onClick={headerBackClickHandler}
                        >
                            <i
                                className='c-btn__icon c-icon c-icon--[semantic-back]'
                                aria-hidden='true'
                                role='img'
                            ></i>
                            <span className='c-btn__text'>
                                {ismUpdateUser.HEADER_BACK}
                            </span>
                        </button>
                    </div>
                </div>
                <div className='edit-user-container__body'>
                    {loading ? (
                        <div className='edit-user__spinner'>
                            <Spinner />
                        </div>
                    ) : (
                        <>
                            {isSuccess && (
                                <Notification
                                    content={successMessage}
                                    type={successNotificationType}
                                    visibility={isSuccess}
                                    setVisibility={setIsSuccess}
                                    withoutClose={false}
                                />
                            )}
                            {isError && (
                                <Notification
                                    content={ismUpdateUser.NOTIFICATION_ERROR}
                                    type={NotificationType.ERROR}
                                    visibility={isError}
                                    setVisibility={setIsError}
                                    withoutClose={true}
                                />
                            )}

                            <div className='edit-user-container__content'>
                                <div
                                    className={
                                        'edit-user-container__profile' +
                                        (!isUserActive ? ' disabled' : '')
                                    }
                                >
                                    <div
                                        className={
                                            'edit-user-container__profile-header' +
                                            (!isUserActive ? ' disabled' : '')
                                        }
                                    >
                                        <div>
                                            {ismUpdateUser.PROFILE_HEADER}
                                        </div>
                                        <div className='button-container container-only-child'>
                                            <span
                                                className='options-icon'
                                                onClick={() =>
                                                    setOpenDrop(true)
                                                }
                                            >
                                                <ProfileOptionsIcon />
                                            </span>
                                            <Dropdown
                                                dropdown={openDrop}
                                                showSelected={false}
                                                showSelectedIcon={false}
                                                handlerChange={(
                                                    value: boolean,
                                                ) => {
                                                    setOpenDrop(value);
                                                }}
                                            >
                                                <MenuItem
                                                    active={isUserActive}
                                                    suspendLabel={
                                                        ismUpdateUser.MENU_SUSPEND
                                                    }
                                                    reinstateLabel={
                                                        ismUpdateUser.MENU_REINSTATE
                                                    }
                                                    deleteLabel={
                                                        ismUpdateUser.MENU_DELETE
                                                    }
                                                    suspendHandler={
                                                        handleSuspendModalOpen
                                                    }
                                                    reinstateHandler={
                                                        handleReinstateModalOpen
                                                    }
                                                    deleteHandler={
                                                        handleDeleteModalOpen
                                                    }
                                                />
                                            </Dropdown>
                                        </div>
                                    </div>

                                    <form
                                        className='edit-user-container__profile--form'
                                        onSubmit={handleSubmit}
                                    >
                                        <Input
                                            labelName={
                                                ismAddNewUser.FORM_LABEL_FIRSTNAME
                                            }
                                            type={'text'}
                                            name={'firstName'}
                                            maxlength={30}
                                            handlerChange={handleChange}
                                            handlerBlur={handleBlur}
                                            value={values.firstName}
                                            asErrors={errors.firstName}
                                            asTouched={touched.firstName}
                                            asSubmitError={''}
                                        />
                                        <Input
                                            labelName={
                                                ismAddNewUser.FORM_LABEL_LASTNAME
                                            }
                                            type={'text'}
                                            name={'lastName'}
                                            maxlength={30}
                                            handlerChange={handleChange}
                                            handlerBlur={handleBlur}
                                            value={values.lastName}
                                            asErrors={errors.lastName}
                                            asTouched={touched.lastName}
                                            asSubmitError={''}
                                        />
                                        <Input
                                            labelName={
                                                ismAddNewUser.FORM_LABEL_EMAIL
                                            }
                                            type={'text'}
                                            name={'email'}
                                            maxlength={50}
                                            handlerChange={handleChange}
                                            handlerBlur={handleBlur}
                                            value={values.email}
                                            asErrors={errors.email}
                                            asTouched={touched.email}
                                            asSubmitError={''}
                                            disabled={true}
                                        />
                                        <div className='edit-user-container__country-phone-row'>
                                            <div className='edit-user-container__country-field'>
                                                <label className='c-form-field__label'>
                                                    {
                                                        systemAdmin.SYSTEM_ADMIN_PAGE_COUNTRY_LABEL
                                                    }
                                                    <span>*</span>
                                                </label>
                                                <div
                                                    onClick={() =>
                                                        setIsCountryTouched(
                                                            true,
                                                        )
                                                    }
                                                >
                                                    <ComboBox
                                                        name={
                                                            'ismCountrySelect'
                                                        }
                                                        options={
                                                            countryOptionLabels
                                                        }
                                                        placeholder={''}
                                                        defaultValue={''}
                                                        onSelect={
                                                            handleCountrySelect
                                                        }
                                                        selectedOption={
                                                            selectedCountryLabel
                                                        }
                                                        multiselectable={false}
                                                        disabled={false}
                                                        hasError={
                                                            countryHasError
                                                        }
                                                    />
                                                </div>
                                                {countryErrorMessage && (
                                                    <span
                                                        className={
                                                            'c-error-message'
                                                        }
                                                    >
                                                        {countryErrorMessage}
                                                    </span>
                                                )}
                                            </div>
                                            <Input
                                                labelName={
                                                    ismAddNewUser.FORM_LABEL_PHONE
                                                }
                                                type={'text'}
                                                name={'phone'}
                                                maxlength={12}
                                                handlerChange={
                                                    onChangePhoneHandler
                                                }
                                                handlerBlur={handleBlur}
                                                value={values.phone}
                                                asErrors={errors.phone}
                                                asTouched={touched.phone}
                                                asSubmitError={''}
                                                handleKeyPress={
                                                    onKeyPressPhoneHandler
                                                }
                                                description={
                                                    ismUpdateUser.PHONE_DESCRIPTION
                                                }
                                            />
                                        </div>

                                        <div className='edit-user-container__section-header'>
                                            {ismAddNewUser.PERMISSIONS_HEADER}
                                        </div>
                                        <p className='edit-user-container__section-description'>
                                            {
                                                ismAddNewUser.PERMISSIONS_DESCRIPTION
                                            }
                                        </p>
                                        <div className='edit-user-container__permissions-grid'>
                                            <div className='edit-user-container__permissions-col'>
                                                {permissionGroups.leftCol.map(
                                                    (group) => (
                                                        <React.Fragment
                                                            key={`left-${group.title}`}
                                                        >
                                                            {renderPermissionGroup(
                                                                group.title,
                                                                group.items,
                                                            )}
                                                        </React.Fragment>
                                                    ),
                                                )}
                                            </div>
                                            <div className='edit-user-container__permissions-col'>
                                                {permissionGroups.rightCol.map(
                                                    (group) => (
                                                        <React.Fragment
                                                            key={`right-${group.title}`}
                                                        >
                                                            {renderPermissionGroup(
                                                                group.title,
                                                                group.items,
                                                            )}
                                                        </React.Fragment>
                                                    ),
                                                )}
                                            </div>
                                        </div>

                                        <div className='edit-user--form_button-ct'>
                                            <button
                                                className={`edit-user--form_button secondary c-btn ${brand}`}
                                                type='button'
                                                onClick={headerBackClickHandler}
                                            >
                                                <span className='c-btn__text'>
                                                    {
                                                        ismUpdateUser.BUTTON_CANCEL
                                                    }
                                                </span>
                                            </button>
                                            <button
                                                className={`edit-user--form_button c-btn ${brand}`}
                                                type='submit'
                                                disabled={isActionDisabled}
                                            >
                                                <span className='c-btn__text'>
                                                    {updateLoading ? (
                                                        <Spinner />
                                                    ) : (
                                                        editUser.BUTTON_SAVE_LABEL
                                                    )}
                                                </span>
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

function selectPhoneNumber(
    phoneNumbers: Array<{
        areaCode: string;
        contactNumber: string;
        country?: string;
        countryCode?: string;
        telecomunicationId: number;
        telecomunicationType: number;
    }>,
): string {
    let phoneNumber = '';
    const EMPTY_ARRAY = 0;

    if (phoneNumbers && phoneNumbers.length > EMPTY_ARRAY) {
        phoneNumber = `${phoneNumbers[0].areaCode}${phoneNumbers[0].contactNumber}`;
    }

    return phoneNumber;
}

function selectPhoneCountry(
    phoneNumbers: Array<{
        areaCode: string;
        contactNumber: string;
        country?: string;
        countryCode?: string;
        telecomunicationId: number;
        telecomunicationType: number;
    }>,
): string {
    const EMPTY_ARRAY = 0;

    if (!phoneNumbers || phoneNumbers.length <= EMPTY_ARRAY) {
        return '';
    }

    const firstPhoneCountry =
        phoneNumbers[0].countryCode || phoneNumbers[0].country || '';
    const normalizedCountry = firstPhoneCountry.toUpperCase();

    if (normalizedCountry === 'US' || normalizedCountry === 'USA') {
        return 'USA';
    }

    if (normalizedCountry === 'CA' || normalizedCountry === 'CANADA') {
        return 'Canada';
    }

    return '';
}

function mapUserDataResponse(data: any): any {
    const {
        dsourceProfile,
        userProfile,
        userPermissions,
        associations,
        ldapLockStatus,
    } = data;
    const phone = selectPhoneNumber(dsourceProfile.user.phoneNumbers);
    const country = selectPhoneCountry(dsourceProfile.user.phoneNumbers);

    const user = {
        firstName: dsourceProfile.user.firstName || '',
        lastName: dsourceProfile.user.lastName || '',
        country,
        phone,
        email: userProfile.userLoginId,
        partyId: userProfile.userPartyId,
        userId: userProfile.userId,
        userStatus: userProfile.userStatus,
        userPermissions: userPermissions,
        associations: associations,
        isUserLocked: ldapLockStatus.isUserLocked,
        regions: [],
        districts: [],
        userIsFieldAccessUser: userProfile.userIsFieldAccessUser,
    };

    if (!user.email || !user.partyId || !user.userId) {
        throw Error();
    }

    return user;
}

function isStatusActive(status: number): boolean {
    const ACTIVE_STATUS_NUMBER = 0;
    return status == ACTIVE_STATUS_NUMBER;
}

export default ISMUpdateUser;
